// src/components/WarehousesInstructions.js
// Warehouses list — card anatomy (header: title + count + search + create),
// geometry-contract table, URL-persisted list state, row click → detail page
// (/warehouses-instructions/:warehouseId). Create happens in a sectioned
// modal shared conceptually with the detail page's edit form.
// API: /api/logistics/warehouses-edn → [{id, name, email, emails[],
// instructions, clients[{id,name}], client_count, created_at, updated_at}].

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaTimes, FaPlus, FaSearch, FaCheck, FaChevronRight,
  FaCheckCircle, FaTimesCircle, FaSpinner, FaExclamationTriangle, FaWarehouse
} from 'react-icons/fa';
import './WarehousesInstructions.css';

export const getWarehouseEmails = (warehouse) => {
  if (Array.isArray(warehouse?.emails) && warehouse.emails.length > 0) {
    return warehouse.emails.filter(Boolean);
  }
  return warehouse?.email ? [warehouse.email] : [];
};

const EMAIL_RE = /^\S+@\S+\.\S+$/;

// Multi-email editor shared by the create modal and the detail edit form.
export const EmailListEditor = ({ emails, onChange, t }) => {
  const list = emails.length > 0 ? emails : [''];
  const setAt = (index, value) => onChange(list.map((e, i) => (i === index ? value : e)));
  const removeAt = (index) => {
    const next = list.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : ['']);
  };
  return (
    <div className="wh-email-editor">
      {list.map((email, index) => (
        <div key={index} className="wh-email-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setAt(index, e.target.value)}
            placeholder="contact@entrepot.fr"
          />
          <button
            type="button"
            className="wh-email-remove"
            onClick={() => removeAt(index)}
            disabled={list.length === 1 && !list[0]}
            aria-label={t('warehousesInstructions.removeEmail', 'Retirer cet email')}
          >
            <FaTimes />
          </button>
        </div>
      ))}
      <button type="button" className="wh-email-add" onClick={() => onChange([...list, ''])}>
        <FaPlus /> {t('warehousesInstructions.addEmail', 'Ajouter un email')}
      </button>
    </div>
  );
};

// Client picker shared by the create modal and the detail edit form.
export const ClientPicker = ({ clients, selectedIds, onToggle, t }) => {
  const [query, setQuery] = useState('');
  const q = query.toLowerCase();
  const filtered = clients.filter((client) =>
    (client.name || '').toLowerCase().includes(q) ||
    (client.client_id || '').toLowerCase().includes(q));
  return (
    <div className="wh-client-picker">
      <div className="wh-client-picker-head">
        <div className="wh-client-picker-search">
          <FaSearch aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('warehousesInstructions.searchClients', 'Rechercher un client…')}
          />
        </div>
        <span className="wh-client-picker-count">
          {t('warehousesInstructions.selectedCount', '{{total}} sélectionnés', { total: selectedIds.length })}
        </span>
      </div>
      <div className="wh-client-picker-list">
        {filtered.length === 0 ? (
          <div className="wh-client-picker-empty">
            {t('warehousesInstructions.noClientsFound', 'Aucun client trouvé')}
          </div>
        ) : (
          filtered.map((client) => {
            const isOn = selectedIds.includes(client.id);
            return (
              <button
                key={client.id}
                type="button"
                className={`wh-client-option ${isOn ? 'on' : ''}`}
                aria-pressed={isOn}
                onClick={() => onToggle(client.id)}
              >
                <span className="wh-client-option-check">{isOn && <FaCheck />}</span>
                <span className="wh-client-option-name">{client.name}</span>
                {client.client_id && <span className="wh-client-option-id">{client.client_id}</span>}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export const validateWarehouseForm = (form, t) => {
  if (!form.name.trim()) {
    return t('warehousesInstructions.nameRequired', 'Le nom est requis.');
  }
  const bad = form.emails.map((e) => e.trim()).filter(Boolean).find((e) => !EMAIL_RE.test(e));
  if (bad) {
    return t('warehousesInstructions.invalidEmail', 'Email invalide : {{email}}', { email: bad });
  }
  return null;
};

export const warehousePayload = (form) => {
  const emails = [];
  form.emails.map((e) => e.trim()).filter(Boolean).forEach((e) => {
    if (!emails.some((x) => x.toLowerCase() === e.toLowerCase())) emails.push(e);
  });
  return {
    name: form.name.trim(),
    email: emails[0] || '',
    emails,
    instructions: form.instructions,
    client_ids: form.clientIds
  };
};

const emptyForm = () => ({ name: '', emails: [''], instructions: '', clientIds: [] });

const WarehousesInstructions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [warehouses, setWarehouses] = useState(null); // null = loading
  const [clients, setClients] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  const PAGE_SIZE = 25;
  const searchQuery = searchParams.get('q') || '';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const requestedPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const updateListParams = (updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace });
  };

  const showFeedback = useCallback((type, text) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, text });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
  }, []);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    axios.get('/api/logistics/warehouses-edn', { headers })
      .then((res) => setWarehouses(res.data))
      .catch((error) => {
        console.error('Failed to fetch warehouses:', error);
        setWarehouses([]);
        showFeedback('error', t('warehousesInstructions.fetchError', 'Impossible de charger les entrepôts.'));
      });
    axios.get('/api/logistics/clients-edn', { headers })
      .then((res) => setClients(res.data))
      .catch((error) => console.error('Failed to fetch clients:', error));
  }, [showFeedback, t]);

  const loading = warehouses === null;
  const list = warehouses || [];

  const q = searchQuery.toLowerCase();
  const filtered = list.filter((warehouse) => {
    const name = (warehouse.name || '').toLowerCase();
    const emails = getWarehouseEmails(warehouse).join(' ').toLowerCase();
    const clientNames = (warehouse.clients || []).map((c) => c.name).join(' ').toLowerCase();
    return name.includes(q) || emails.includes(q) || clientNames.includes(q);
  });
  const sorted = [...filtered].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }));

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(requestedPage, totalPages);
  const paginated = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getPageItems = () => {
    const anchors = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const pages = [...anchors].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const items = [];
    let prev = 0;
    pages.forEach((p) => {
      if (p - prev > 1) items.push(`gap-${p}`);
      items.push(p);
      prev = p;
    });
    return items;
  };

  const handleCreate = async () => {
    const error = validateWarehouseForm(createForm, t);
    if (error) { setCreateError(error); return; }
    setCreating(true);
    setCreateError(null);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const res = await axios.post('/api/logistics/warehouses-edn', warehousePayload(createForm), { headers });
      setShowCreateModal(false);
      setCreateForm(emptyForm());
      showFeedback('success', t('warehousesInstructions.createSuccess', 'Entrepôt créé.'));
      const listRes = await axios.get('/api/logistics/warehouses-edn', { headers });
      setWarehouses(listRes.data);
      if (res.data?.id) navigate(`/warehouses-instructions/${res.data.id}`);
    } catch (error) {
      console.error('Failed to create warehouse:', error);
      setCreateError(error.response?.data?.detail || t('warehousesInstructions.createError', 'Échec de la création de l’entrepôt.'));
    } finally {
      setCreating(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm(emptyForm());
    setCreateError(null);
  };

  const toggleCreateClient = (clientId) => {
    setCreateForm((prev) => ({
      ...prev,
      clientIds: prev.clientIds.includes(clientId)
        ? prev.clientIds.filter((id) => id !== clientId)
        : [...prev.clientIds, clientId]
    }));
  };

  return (
    <div className="warehouses-page">
      <div className="wh-card">
        {/* Header: title + count | search + create */}
        <div className="wh-card-header">
          <div className="wh-header-left">
            <h1>{t('warehousesInstructions.title', 'Entrepôts')}</h1>
            {!loading && (
              <span className="wh-count">
                {t('warehousesInstructions.countLabel', '{{total}} entrepôts', { total: list.length })}
              </span>
            )}
          </div>
          <div className="wh-header-right">
            <div className="wh-search">
              <FaSearch aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => updateListParams({ q: e.target.value, page: null }, { replace: true })}
                placeholder={t('warehousesInstructions.searchPlaceholder', 'Rechercher un entrepôt, un email, un client…')}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="wh-search-clear"
                  onClick={() => updateListParams({ q: null, page: null }, { replace: true })}
                  aria-label={t('common.cancel', 'Effacer')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            <button type="button" className="u-btn u-btn--primary" onClick={() => setShowCreateModal(true)}>
              <FaPlus /> {t('warehousesInstructions.createNew', 'Créer un entrepôt')}
            </button>
          </div>
        </div>

        {feedback && (
          <div className={`wh-feedback ${feedback.type}`} role="status">
            {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            {feedback.text}
          </div>
        )}

        {loading && (
          <div className="wh-skeleton" aria-hidden="true">
            {Array.from({ length: 6 }, (_, i) => <div key={i} className="wh-skeleton-row" />)}
          </div>
        )}

        {!loading && list.length === 0 && (
          <div className="wh-empty">
            <FaWarehouse className="wh-empty-icon" />
            <p>{t('warehousesInstructions.noWarehouses', 'Aucun entrepôt pour le moment. Créez-en un pour que l’IA sache qui contacter et comment.')}</p>
          </div>
        )}

        {!loading && list.length > 0 && filtered.length === 0 && (
          <div className="wh-empty">
            <p>{t('warehousesInstructions.noMatches', 'Aucun entrepôt ne correspond à la recherche.')}</p>
          </div>
        )}

        {!loading && paginated.length > 0 && (
          <div className="wh-table-scroll">
            <table className="wh-table">
              <colgroup>
                <col style={{ width: '24%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '40px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t('warehousesInstructions.name', 'Nom')}</th>
                  <th>{t('warehousesInstructions.contact', 'Contact')}</th>
                  <th>{t('warehousesInstructions.instructions', 'Instructions')}</th>
                  <th>{t('warehousesInstructions.clients', 'Clients')}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {paginated.map((warehouse) => {
                  const emails = getWarehouseEmails(warehouse);
                  const clientList = warehouse.clients || [];
                  const clientNames = clientList.map((c) => c.name).join(', ');
                  return (
                    <tr
                      key={warehouse.id}
                      className="wh-row"
                      tabIndex={0}
                      onClick={() => navigate(`/warehouses-instructions/${warehouse.id}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/warehouses-instructions/${warehouse.id}`); }}
                    >
                      <td className="wh-cell-name" title={warehouse.name}>{warehouse.name}</td>
                      <td className="wh-cell-contact" title={emails.join(', ')}>
                        {emails.length === 0 ? (
                          <span className="wh-flag-warn">
                            <FaExclamationTriangle /> {t('warehousesInstructions.noEmail', 'Aucun email')}
                          </span>
                        ) : (
                          <>
                            <span className="wh-contact-email">{emails[0]}</span>
                            {emails.length > 1 && <span className="wh-more-chip">+{emails.length - 1}</span>}
                          </>
                        )}
                      </td>
                      <td className="wh-cell-instructions">
                        {warehouse.instructions ? (
                          <span className="wh-flag-ok">
                            <FaCheck /> {t('warehousesInstructions.instructionsSet', 'Définies')}
                          </span>
                        ) : (
                          <span className="wh-flag-warn">
                            <FaExclamationTriangle /> {t('warehousesInstructions.instructionsMissing', 'À compléter')}
                          </span>
                        )}
                      </td>
                      <td className="wh-cell-clients" title={clientNames}>
                        {clientList.length === 0 ? (
                          <span className="wh-muted-dash">—</span>
                        ) : (
                          <>
                            <span className="wh-clients-count">{clientList.length}</span>
                            <span className="wh-clients-names">{clientNames}</span>
                          </>
                        )}
                      </td>
                      <td className="wh-cell-chevron"><FaChevronRight aria-hidden="true" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="wh-card-footer">
            <span className="wh-footer-count">
              {t('warehousesInstructions.matchCount', '{{total}} résultats', { total: filtered.length })}
            </span>
            <nav className="wh-pager" aria-label={t('warehousesInstructions.pagination', 'Pagination')}>
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => updateListParams({ page: currentPage - 1 <= 1 ? null : currentPage - 1 })}
                aria-label={t('common.previous')}
              >
                ‹
              </button>
              {getPageItems().map((item) => (
                typeof item === 'number' ? (
                  <button
                    key={item}
                    type="button"
                    className={item === currentPage ? 'active' : ''}
                    aria-current={item === currentPage ? 'page' : undefined}
                    onClick={() => updateListParams({ page: item <= 1 ? null : item })}
                  >
                    {item}
                  </button>
                ) : (
                  <span key={item} className="wh-pager-ellipsis" aria-hidden="true">…</span>
                )
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => updateListParams({ page: currentPage + 1 })}
                aria-label={t('common.next')}
              >
                ›
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Create modal — sectioned */}
      {showCreateModal && (
        <div className="wh-modal-backdrop" onClick={closeCreateModal}>
          <div className="wh-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="wh-modal-header">
              <h3>{t('warehousesInstructions.createNew', 'Créer un entrepôt')}</h3>
              <button type="button" className="wh-modal-close" onClick={closeCreateModal} aria-label={t('common.cancel', 'Annuler')}>
                <FaTimes />
              </button>
            </div>
            <div className="wh-modal-body">
              <div className="wh-form-section">
                <h4>{t('warehousesInstructions.sectionIdentity', 'Identité')}</h4>
                <div className="wh-field">
                  <label htmlFor="wh-name">{t('warehousesInstructions.name', 'Nom')} *</label>
                  <input
                    id="wh-name"
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t('warehousesInstructions.namePlaceholder', 'Ex. : Entrepôt Lyon Sud')}
                    autoFocus
                  />
                </div>
              </div>

              <div className="wh-form-section">
                <h4>{t('warehousesInstructions.sectionContact', 'Contact')}</h4>
                <p className="wh-section-hint">
                  {t('warehousesInstructions.sectionContactHint', 'Adresses utilisées par l’IA pour contacter l’entrepôt.')}
                </p>
                <EmailListEditor
                  emails={createForm.emails}
                  onChange={(emails) => setCreateForm((f) => ({ ...f, emails }))}
                  t={t}
                />
              </div>

              <div className="wh-form-section">
                <h4>{t('warehousesInstructions.sectionInstructions', 'Instructions IA')}</h4>
                <p className="wh-section-hint">
                  {t('warehousesInstructions.sectionInstructionsHint', 'Consignes suivies par l’IA lors des échanges avec cet entrepôt.')}
                </p>
                <textarea
                  rows={6}
                  value={createForm.instructions}
                  onChange={(e) => setCreateForm((f) => ({ ...f, instructions: e.target.value }))}
                  placeholder={t('warehousesInstructions.instructionsPlaceholder', 'Ex. : Toujours fournir la référence de commande…')}
                />
              </div>

              <div className="wh-form-section">
                <h4>{t('warehousesInstructions.sectionClients', 'Clients associés')}</h4>
                <ClientPicker
                  clients={clients}
                  selectedIds={createForm.clientIds}
                  onToggle={toggleCreateClient}
                  t={t}
                />
              </div>

              {createError && <div className="wh-modal-error">{createError}</div>}
            </div>
            <div className="wh-modal-footer">
              <button type="button" className="u-btn u-btn--secondary" onClick={closeCreateModal} disabled={creating}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={handleCreate} disabled={creating}>
                {creating
                  ? (<><FaSpinner className="wh-spin" /> {t('warehousesInstructions.creating', 'Création…')}</>)
                  : t('warehousesInstructions.submitCreate', 'Créer l’entrepôt')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehousesInstructions;
